import { useEffect, useState } from 'react';
import { Search, X } from 'lucide-react';

import { cn } from '../../../lib/cn';
import type { LeadDisplayStatus, LeadFilters as LeadFiltersValue, LeadScoreClass, LeadSourceRow } from './leads.schema';
import styles from './leads.module.css';

const STATUS_OPTIONS: { value: LeadDisplayStatus; label: string }[] = [
  { value: 'new', label: 'חדש' },
  { value: 'contacted', label: 'יצרנו קשר' },
  { value: 'qualified', label: 'הוסמך' },
  { value: 'disqualified', label: 'לא רלוונטי' },
  { value: 'converted', label: 'הומר ל-Deal' },
];

const SCORE_OPTIONS: { value: LeadScoreClass; label: string }[] = [
  { value: 'hot', label: 'חם' },
  { value: 'warm', label: 'חמים' },
  { value: 'cold', label: 'קר' },
  { value: null, label: 'ללא ציון' },
];

function toggleValue<T>(items: T[], value: T): T[] {
  return items.includes(value) ? items.filter((item) => item !== value) : [...items, value];
}

function hasActiveFilters(filters: LeadFiltersValue): boolean {
  return Boolean(
    filters.search.trim() ||
      filters.statuses.length ||
      filters.sourceIds.length ||
      filters.scoreClasses.length,
  );
}

interface LeadFiltersProps {
  filters: LeadFiltersValue;
  sources: LeadSourceRow[];
  onChange: (filters: LeadFiltersValue) => void;
}

export function LeadFilters({ filters, sources, onChange }: LeadFiltersProps) {
  const [searchValue, setSearchValue] = useState(filters.search);

  useEffect(() => {
    setSearchValue(filters.search);
  }, [filters.search]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (searchValue !== filters.search) {
        onChange({ ...filters, search: searchValue });
      }
    }, 300);

    return () => {
      window.clearTimeout(timer);
    };
  }, [filters, onChange, searchValue]);

  const active = hasActiveFilters(filters);

  return (
    <div className={styles.filterBar}>
      <label className={styles.searchBox}>
        <span className={styles.srOnly}>חיפוש</span>
        <input
          type="search"
          value={searchValue}
          aria-label="חיפוש לפי שם / חברה / אימייל"
          onChange={(event) => {
            setSearchValue(event.target.value);
          }}
        />
        <Search size={16} aria-hidden="true" />
      </label>

      <div className={styles.filterGroup}>
        <span className={styles.filterLabel}>סטטוס</span>
        <div className={styles.chips}>
          {STATUS_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={cn(styles.chip, filters.statuses.includes(option.value) && styles.chipActive)}
              onClick={() => {
                onChange({ ...filters, statuses: toggleValue(filters.statuses, option.value) });
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.filterGroup}>
        <span className={styles.filterLabel}>מקור</span>
        <div className={styles.chips}>
          <button
            type="button"
            className={cn(styles.chip, filters.sourceIds.includes(null) && styles.chipActive)}
            onClick={() => {
              onChange({ ...filters, sourceIds: toggleValue(filters.sourceIds, null) });
            }}
          >
            ללא מקור
          </button>
          {sources.map((source) => (
            <button
              key={source.id}
              type="button"
              className={cn(styles.chip, filters.sourceIds.includes(source.id) && styles.chipActive)}
              onClick={() => {
                onChange({ ...filters, sourceIds: toggleValue(filters.sourceIds, source.id) });
              }}
            >
              {source.name}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.filterGroup}>
        <span className={styles.filterLabel}>ציון</span>
        <div className={styles.chips}>
          {SCORE_OPTIONS.map((option) => (
            <button
              key={option.value ?? 'none'}
              type="button"
              className={cn(styles.chip, filters.scoreClasses.includes(option.value) && styles.chipActive)}
              onClick={() => {
                onChange({ ...filters, scoreClasses: toggleValue(filters.scoreClasses, option.value) });
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {active ? (
        <button
          type="button"
          className={styles.clearFilters}
          onClick={() => {
            setSearchValue('');
            onChange({ search: '', statuses: [], sourceIds: [], scoreClasses: [] });
          }}
        >
          <X size={15} aria-hidden="true" />
          נקה סינון
        </button>
      ) : null}
    </div>
  );
}
